import moment from 'moment';
import { getUIFieldTypeFromDataElement } from 'dsilo-ui-components';

import { isConfigEnabled } from './config/isConfigEnabled';
import { isApiFetchSelectValueEmpty } from '../TrackerViewerV2/utils/apiFetchSelect';
import { formatConfiguredAmountValue, enrichNumberElementForInlineEdit } from '../TrackerViewerV2/utils/formatters';
import { formatDealReviewHistoryTimePST } from '../TrackerViewer/utils';
import {
    getNegotiationStatusDependencyFields,
    getNegotiationStatusOptions,
    NEGOTIATION_STATUS_FIELD,
} from './negotiationStatusRules';
import { getTierControlField } from './tierRules';
import {
    getTierResetConfigFieldKeys,
    isTierResetEnabledForField,
    resolveTierResetConfig,
} from './tierResetRules';

export {
    TOP_TIER_REQUIRED_FIELD,
    normalizeTopTierRequired,
    getFieldTier,
    getTierControlField,
    isFieldEditableForTopTier,
    getTierRestrictionMessage,
    validateTierReviewDates,
    getTierDateValidationMode,
    getTierDateMessageForField,
    clearRestrictedTierValues,
    shouldClearOnControlFieldSave,
} from './tierRules';

export {
    APPROVAL_DATE_FIELD,
    NEGOTIATION_STATUS_FIELD,
    DEAL_REVIEW_STATUS,
    NEW_NEGOTIATION_STATUS,
    NEGOTIATION_IN_PROGRESS_STATUS,
    NEGOTIATION_COMPLETED_STATUS,
    NEGOTIATION_COMPLETE_STATUS,
    NEGOTIATION_CANCELLED_STATUS,
    NEGOTIATION_STATUS_DEPENDENCY_FIELDS,
    hasApprovalDate,
    isApprovalDateFilled,
    getDefaultNegotiationStatus,
    getNegotiationStatusOptions,
    getTopTierApprovalDateKey,
    deriveNegotiationStatus,
    hasRelevantTopTierApprovalDate,
    getNegotiationStatusAutoUpdatePayload,
    getNegotiationStatusDependencyFields,
    getManualNegotiationStatusValidationError,
} from './negotiationStatusRules';

export {
    tierResetConfigMatches,
    getTierResetDecisionFieldForButton,
    getTierResetFieldsForRow,
    getTierResetConfigFieldKeys,
    isTierResetEnabledForField,
    resolveTierResetConfig,
} from './tierResetRules';

/**
 * Returns true when a column is flagged to show tier reset in selectedDataElement.
 */
export const isTierResetColumn = (column = {}, behaviorConfig) => {
    if (!isConfigEnabled(behaviorConfig?.columns?.tierResetIcon)) {
        return false;
    }

    const flagKey = behaviorConfig?.columns?.tierResetIcon?.flagKey || 'showTierReset';
    const flag = column[flagKey];

    return flag === true || flag === 'true' || flag === 'True';
};

/**
 * Returns true when a column should be hidden from the table UI.
 */
export const isColumnHidden = (column = {}, behaviorConfig) => {
    if (!isConfigEnabled(behaviorConfig?.columns?.hidden, true)) {
        return false;
    }

    const flagKey = behaviorConfig?.columns?.hidden?.flagKey || 'isHide';
    const flag = column[flagKey];

    return flag === true || flag === 'true' || flag === 'True';
};

/**
 * Returns true when a column is configured to show field history.
 */
export const isHistoryEnabledColumn = (column = {}, behaviorConfig) => {
    if (!isConfigEnabled(behaviorConfig?.features?.fieldHistory)) {
        return false;
    }

    if (!isConfigEnabled(behaviorConfig?.columns?.history)) {
        return false;
    }

    const requiredFlags = behaviorConfig?.columns?.history?.requiredFlags || {
        isHistory: true,
        showHistroy: true,
    };

    return Object.entries(requiredFlags).every(([flagKey, expected]) => {
        if (!expected) {
            return true;
        }

        const flag = column[flagKey];
        return flag === true || flag === 'true' || flag === 'True';
    });
};

/**
 * Resolves the history array key for a configured field.
 */
export const getFieldHistoryKey = (column = {}, behaviorConfig) => {
    const fieldKey = getCellFieldKey(column);
    const suffix = behaviorConfig?.columns?.history?.historyKeySuffix || 'History';
    return fieldKey ? `${fieldKey}${suffix}` : null;
};

/**
 * Builds payload fragment that appends a field-history entry when history is enabled for the column.
 * Mirrors TrackerViewer field history tracking on value change.
 */
export const buildFieldHistoryAppendPayload = (
    row = {},
    column = {},
    value,
    updatedBy = 'Unknown',
    behaviorConfig,
) => {
    if (!isHistoryEnabledColumn(column, behaviorConfig)) {
        return {};
    }

    const historyKey = getFieldHistoryKey(column, behaviorConfig);
    if (!historyKey) {
        return {};
    }

    const existingHistory = Array.isArray(row[historyKey]) ? row[historyKey] : [];

    return {
        [historyKey]: [
            ...existingHistory,
            {
                value: value || '',
                updatedAtPST: formatDealReviewHistoryTimePST(new Date()),
                updatedBy: updatedBy || 'Unknown',
            },
        ],
    };
};

/**
 * Returns visible table columns (excludes isHide columns).
 */
export const getVisibleColumnHeaders = (blockHeaders = [], behaviorConfig) => (
    blockHeaders.filter((column) => !isColumnHidden(column, behaviorConfig))
);

/**
 * Returns true when a column is configured to stay pinned during horizontal scroll.
 */
export const isColumnSticky = (column = {}) => {
    const flag = column.isSticky;

    return flag === true || flag === 'true' || flag === 'True';
};

/**
 * Reorders columns so every `isSticky` column comes first (relative order preserved
 * within each group), without mutating the input array.
 */
export const getStickyOrderedColumns = (columns = []) => {
    const stickyColumns = columns.filter((column) => isColumnSticky(column));
    const nonStickyColumns = columns.filter((column) => !isColumnSticky(column));

    return [...stickyColumns, ...nonStickyColumns];
};

/**
 * Builds the comma-separated fields string for table API requests.
 */
export const buildFieldsFromSelectedElements = (
    selectedDataElement = [],
    behaviorConfig,
) => {
    const fieldNames = [];
    const tierResetConfig = behaviorConfig?.tierResetLegacy || [];
    const controlField = getTierControlField(behaviorConfig);
    const alwaysIncludeFields = behaviorConfig?.api?.alwaysIncludeFields || [];

    selectedDataElement.forEach((element) => {
        if (element?.value) {
            fieldNames.push(element.value);
        }

        if (element?.name && element.name !== element.value) {
            fieldNames.push(element.name);
        }

        if (isHistoryEnabledColumn(element, behaviorConfig)) {
            const historyKey = getFieldHistoryKey(element, behaviorConfig);
            if (historyKey) {
                fieldNames.push(historyKey);
            }
        }
    });

    if (controlField && !fieldNames.includes(controlField)) {
        fieldNames.push(controlField);
    }

    alwaysIncludeFields.forEach((fieldKey) => {
        fieldNames.push(fieldKey);
    });

    if (isConfigEnabled(behaviorConfig?.negotiationStatus)) {
        getNegotiationStatusDependencyFields(behaviorConfig).forEach((fieldKey) => {
            fieldNames.push(fieldKey);
        });
    }

    getTierResetConfigFieldKeys(tierResetConfig).forEach((fieldKey) => {
        fieldNames.push(fieldKey);
    });

    return [...new Set(fieldNames)].filter(Boolean).join(',');
};

/**
 * Merges chart-level conditions with runtime filter conditions.
 */
export const mergeTableConditions = (baseConditions = [], filterConditions = []) => {
    return [...(baseConditions || []), ...(filterConditions || [])];
};

/**
 * Normalizes chart sort config to a single-field API sort: { fieldName: 1 | -1 }.
 */
export const normalizeChartSort = (sortConfig) => {
    if (!sortConfig || typeof sortConfig !== 'object' || Array.isArray(sortConfig)) {
        return {};
    }

    const sortEntry = Object.entries(sortConfig).find(([, direction]) => {
        const numericDirection = Number(direction);
        return numericDirection === 1 || numericDirection === -1;
    });

    if (!sortEntry) {
        return {};
    }

    const [fieldKey, direction] = sortEntry;
    if (!fieldKey) {
        return {};
    }

    return {
        [fieldKey]: Number(direction) === -1 ? -1 : 1,
    };
};

/**
 * Applies finalized negotiationStatus dropdown options to the column config.
 */
const applyNegotiationStatusOptions = (column = {}, behaviorConfig) => {
    const columnKey = getCellFieldKey(column);
    const statusField = behaviorConfig?.negotiationStatus?.field || NEGOTIATION_STATUS_FIELD;

    if (columnKey !== statusField || !isConfigEnabled(behaviorConfig?.negotiationStatus)) {
        return column;
    }

    return {
        ...column,
        options: getNegotiationStatusOptions(behaviorConfig),
    };
};

/**
 * Enriches configured column definitions with data-model metadata.
 */
export const enrichColumnHeaders = (
    selectedDataElement = [],
    dataElements = [],
    behaviorConfig,
) => {
    if (!selectedDataElement?.length) {
        return [];
    }

    return JSON.parse(JSON.stringify(selectedDataElement)).map((column) => {
        const columnKey = column.value || column.name;
        const schemaElement = dataElements.find(
            (dataElement) => dataElement.name === columnKey
                || dataElement.name === column.value
                || dataElement.name === column.name,
        );

        if (!schemaElement?.name) {
            return applyNegotiationStatusOptions(column, behaviorConfig);
        }

        return applyNegotiationStatusOptions({ ...schemaElement, ...column }, behaviorConfig);
    });
};

/**
 * Resolves column config from enriched headers, falling back to selectedDataElement.
 */
export const resolveColumnConfig = (fieldKey, blockHeaders = [], selectedDataElement = []) => {
    const headerMatch = blockHeaders.find((column) => getCellFieldKey(column) === fieldKey);
    if (headerMatch) {
        return headerMatch;
    }

    return selectedDataElement.find((column) => getCellFieldKey(column) === fieldKey) || null;
};

/**
 * Formats a read-only table cell value (includes amount/currency display rules).
 */
export const formatTableCellDisplayValue = (value, column = {}, behaviorConfig) => (
    formatCellDisplayValue(value, column, behaviorConfig)
);

/**
 * Formats a cell value for read-only table display.
 */
export const formatCellDisplayValue = (value, element = {}, behaviorConfig) => {
    const displayElement = enrichNumberElementForInlineEdit(element);
    const emptyPlaceholder = behaviorConfig?.columns?.display?.emptyPlaceholder || '—';

    if (value === null || value === undefined || value === '') {
        return emptyPlaceholder;
    }

    if (displayElement.type === 'namepicker' && typeof value === 'object') {
        return value.label || value.name || value.value || emptyPlaceholder;
    }

    if (Array.isArray(value)) {
        return value.map((item) => (typeof item === 'object' ? item.label || item.name || item.value : item)).join(', ');
    }

    if (displayElement.type === 'date' || displayElement.type === 'datetime') {
        const parsed = moment.parseZone(value);
        return parsed.isValid() ? parsed.format('L') : String(value);
    }

    const configuredAmountDisplay = formatConfiguredAmountValue(value, displayElement);
    if (configuredAmountDisplay != null) {
        return configuredAmountDisplay;
    }

    if (displayElement.showDollarSymbol || displayElement.showDoller) {
        const numeric = Number(String(value).replace(/[$,]/g, ''));
        return Number.isNaN(numeric) ? String(value) : `$${numeric.toLocaleString()}`;
    }

    return String(value);
};

/**
 * Resolves the data key used for a column/cell.
 */
export const getCellFieldKey = (column = {}) => column.value || column.name;

/**
 * Returns true when a column is configured as non-editable in the admin table.
 */
export const isColumnNonEditable = (column = {}) => {
    const flag = column.isNonEditable;

    return flag === true || flag === 'true' || flag === 'True';
};

/**
 * Returns true when a column is configured as required.
 */
export const isColumnRequired = (column = {}, behaviorConfig) => {
    if (!isConfigEnabled(behaviorConfig?.validation?.required)) {
        return false;
    }

    const flag = column.isRequired ?? column.required;

    return flag === true || flag === 'true' || flag === 'True';
};

/**
 * Returns true when a required field value should be treated as empty.
 */
export const isRequiredFieldValueEmpty = (value, column = {}) => {
    if (column?.isFetchAPI) {
        return isApiFetchSelectValueEmpty(value);
    }

    if (value === null || value === undefined || value === '') {
        return true;
    }

    if (typeof value === 'string' && value.trim() === '') {
        return true;
    }

    if (Array.isArray(value) && value.length === 0) {
        return true;
    }

    if (typeof value === 'object') {
        const innerValue = value.value ?? value.name ?? value.label;
        return innerValue === null || innerValue === undefined || innerValue === '';
    }

    return false;
};

/**
 * Returns the display label used in user-facing messages for a column.
 */
export const getColumnDisplayLabel = (column = {}) => column.label || column.value || column.name || 'this';

/**
 * Returns the toast message when a required field is saved empty.
 */
export const getRequiredFieldValidationMessage = (column = {}, behaviorConfig) => {
    const template = behaviorConfig?.validation?.required?.messageTemplate
        || '{fieldLabel} is required.';

    return template.replace('{fieldLabel}', getColumnDisplayLabel(column));
};

/**
 * Returns the toast message when a non-editable field is clicked.
 */
export const getNonEditableValidationMessage = (column = {}, behaviorConfig) => {
    const template = behaviorConfig?.validation?.nonEditable?.message
        || 'You cannot modify {fieldLabel} because it is not editable.';

    return template.replace('{fieldLabel}', getColumnDisplayLabel(column));
};

/**
 * Compares two cell values for equality before persisting.
 */
export const areCellValuesEqual = (left, right) => {
    if (left === right) {
        return true;
    }
    if (left === null || left === undefined || left === '') {
        return right === null || right === undefined || right === '';
    }
    if (typeof left === 'object' || typeof right === 'object') {
        return JSON.stringify(left) === JSON.stringify(right);
    }
    return String(left) === String(right);
};

/**
 * Ensures cleared select fields persist as null instead of undefined/empty string.
 */
export const normalizeSelectSaveValue = (value, element = {}, behaviorConfig) => {
    if (!isConfigEnabled(behaviorConfig?.validation?.selectClearValue, true)) {
        return value;
    }

    const fieldType = getUIFieldTypeFromDataElement(element);

    if (fieldType !== 'select' && fieldType !== 'namepicker' && !element?.isFetchAPI) {
        return value;
    }

    if (value === undefined || value === '') {
        return behaviorConfig?.validation?.selectClearValue?.emptyAsNull !== false ? null : value;
    }

    return value;
};

/**
 * Field types that save immediately when their value changes.
 */
export const shouldAutoSaveOnChange = (element = {}, behaviorConfig) => {
    if (!isConfigEnabled(behaviorConfig?.table?.editCommit)) {
        return false;
    }

    const editCommit = behaviorConfig.table.editCommit;
    const fieldType = getUIFieldTypeFromDataElement(element);

    switch (fieldType) {
        case 'select':
        case 'namepicker':
            return editCommit.dropdownOnChange !== false;
        case 'checkbox':
            return editCommit.checkboxOnChange !== false;
        default:
            if (element?.isFetchAPI) {
                return editCommit.apiSelectOnChange !== false;
            }
            return false;
    }
};

/**
 * Builds a stable DOM selector token for the active editing cell.
 */
export const getEditingCellAttr = (rowId, fieldKey) => `${rowId}__${fieldKey}`;

/**
 * Returns true when the column renders a dropdown-style editor.
 */
export const isDropdownColumn = (column = {}) => {
    const fieldType = getUIFieldTypeFromDataElement(column);

    return fieldType === 'select'
        || fieldType === 'namepicker'
        || Boolean(column?.isFetchAPI);
};

/**
 * Ignores click-away events that originate from portaled menus/popovers.
 */
export const isIgnoredClickAwayTarget = (target) => {
    if (!(target instanceof Element)) {
        return false;
    }

    return Boolean(
        target.closest('.MuiPopover-root')
        || target.closest('.MuiMenu-paper')
        || target.closest('.MuiMenu-list')
        || target.closest('.MuiDialog-root')
        || target.closest('.MuiPickersPopper-root')
        || target.closest('[role="listbox"]')
        || target.closest('[role="option"]')
        || target.closest('[class*="-menu"]')
        || target.closest('[class*="-Menu"]')
        || target.closest('[id*="react-select"]')
        || target.closest('.api-select-menu-panel'),
    );
};

/**
 * Returns true when Escape should close an overlay first instead of cancelling inline edit.
 * Narrower than isIgnoredClickAwayTarget — does not match closed react-select controls.
 */
export const shouldDeferInlineEditEscapeCancel = (target, isSelectMenuOpen) => {
    if (isSelectMenuOpen) {
        return true;
    }

    if (!(target instanceof Element)) {
        return false;
    }

    if (target.closest('.MuiPickersPopper-root')) {
        return true;
    }

    if (target.closest('.MuiDialog-root')) {
        return true;
    }

    if (target.closest('[role="listbox"]')) {
        return true;
    }

    if (target.closest('.api-select-menu-panel')) {
        return true;
    }

    if (target.closest('.MuiMenu-paper')) {
        return true;
    }

    const popoverRoot = target.closest('.MuiPopover-root');
    if (popoverRoot && popoverRoot.getAttribute('aria-hidden') !== 'true') {
        return true;
    }

    return false;
};

/**
 * Returns true when the key event is Escape.
 */
export const isEscapeKeyEvent = (event) => event?.key === 'Escape' || event?.keyCode === 27;

/**
 * Returns true when a column is sortable per behavior config.
 */
export const isColumnSortable = (column = {}, behaviorConfig) => {
    if (!isConfigEnabled(behaviorConfig?.features?.columnSorting)) {
        return false;
    }

    if (!isConfigEnabled(behaviorConfig?.columns?.sortable)) {
        return false;
    }

    const disableFlagKey = behaviorConfig?.columns?.sortable?.disableFlagKey || 'sort';
    const defaultSortable = behaviorConfig?.columns?.sortable?.defaultSortable !== false;

    if (column[disableFlagKey] === false) {
        return false;
    }

    return defaultSortable;
};
